import * as graphql from 'generated/graphql';
import { useModalLink } from 'navigation/ModalNavigation';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import SidebarSectionHeader from './SectionHeader';
import { SegmentedControl, Skeleton } from '@treams/treams-ui';
import { useLocation } from 'react-router';
import GoalCard from 'goals/GoalCard';
import { sortBy } from 'lodash';
import useViewer from 'packages/kakapo-ui-application/hooks/useViewer';

function CompactGoalsList(props: { userID: string; status: graphql.GoalStatus }) {
	const location = useLocation();

	const goals = graphql.useIndividualGoalsQuery({
		variables: {
			id: props.userID,
			status: props.status,
			last: 10,
		},
	});

	useEffect(() => {
		goals.refetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location]);

	const sortedGoals = sortBy(goals.data?.user.goalsV2.edges ?? [], ({ node }) => -node.order).map(
		({ node }) => node,
	);

	return (
		<>
			{!goals.data && (
				<Skeleton.Repeat count={2}>
					<Box mb={2}>
						<GoalCard.Skeleton small />
					</Box>
				</Skeleton.Repeat>
			)}
			{sortedGoals.map((goal) => (
				<Box key={goal.id} mb={2}>
					<GoalCard modal goal={goal as unknown as graphql.Goal} small owner={false} />
				</Box>
			))}
		</>
	);
}

export default function SidebarGoalsSection(props: { userID: string }) {
	const { t } = useTranslation();

	const [isOpen, setIsOpen] = useState(false);

	const viewer = useViewer();

	const link = useModalLink(
		viewer?.id === props.userID ? '/v2/goals/new' : `/v2/users/${props.userID}/goals/new`,
		true,
	);

	const [tab, setTab] = useState<'active' | 'completed'>('active');

	return (
		<>
			<SidebarSectionHeader isOpen={isOpen} onClick={() => setIsOpen(!isOpen)} createLink={link}>
				{t('conversation.sidebar.goals.title')}
			</SidebarSectionHeader>

			{isOpen && (
				<>
					<Flex mb={2}>
						<SegmentedControl
							options={[
								{ title: t('conversation.peek.goals.active'), value: 'active' },
								{ title: t('conversation.peek.goals.completed'), value: 'completed' },
							]}
							value={tab}
							onChange={(value) => setTab(value as 'active' | 'completed')}
						/>
					</Flex>

					{tab === 'active' && (
						<CompactGoalsList userID={props.userID} status={graphql.GoalStatus.Active} />
					)}
					{tab === 'completed' && (
						<CompactGoalsList userID={props.userID} status={graphql.GoalStatus.Completed} />
					)}
				</>
			)}
		</>
	);
}
